import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import s from './s.module.less';
import { Button, Form, Input, Spin, message } from 'antd';
import { IUpdatePasswordFormData, IUpdateTelFormData } from 'types/provider';
import { updateProviderNotificationConfig } from 'api/provider';
import ProviderStore from 'store/Provider';
import { sendForget } from 'api/public';
import { hasLowerCase, hasNumber, hasSpecialCharacter, hasUpperCase, isValidUSPhoneV2 } from 'utils/form';
import { PasswordError } from 'types/common';
import useProviderNotificationConfig from 'hooks/useProviderNotificationConfig';
import { REQUIRED_FIELD } from 'constants/common';
import SwitchAccount from '../SwitchAccount';
import { trackHeapEvent } from 'utils/analytics';
import useCurrentUser from 'hooks/useCurrentUser';
import { isOpsImpersonate } from 'utils/group';
import { updateUserPhone, changePassword } from 'api/group';

type IProps = {
    isKlarityAccount?: boolean;
    isShowSwitchAccount?: boolean;
};

const MyAccount = ({ isKlarityAccount, isShowSwitchAccount }: IProps) => {
    const [getUser] = ProviderStore.useStore();
    const [notifyInfo, , refetch] = useProviderNotificationConfig();
    const user = getUser('data');

    const [formInstance] = Form.useForm<IUpdatePasswordFormData>();
    const [telFormInstance] = Form.useForm<IUpdateTelFormData>();
    const [edit, setEdit] = useState(false);
    const [editPhone, setEditPhone] = useState(false);
    const [phoneValue, setPhoneValue] = useState('');
    const [showForgetTip, setShowForgetTip] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasSentCheckEmail, setHasSentCheckEmail] = useState(false);
    const [resendButtonDisabled, setResendButtonDisabled] = useState(false);
    const [psw, setPsw] = useState('');
    const [pswError, setPswError] = useState<PasswordError[]>([]);
    const showPwsError = psw && pswError.length > 0;
    const [currentUser, loadingCurrentUser, refetchCurrentUser] = useCurrentUser();

    const [countDown, setCountDown] = useState(0);
    const timer = useRef(60);
    const SI = useRef<ReturnType<typeof setInterval> | null>(null);

    // Under OPS preview the sa-token actor is the OPS user, so currentUser.user
    // is the OPS account, not the provider being previewed. Read the acting
    // identity instead, and keep the section read-only — the password / phone
    // endpoints always write to the actor, so editing here would silently
    // change the OPS user's own credentials.
    const isOpsPreview = isOpsImpersonate(currentUser);
    const identity = isOpsPreview ? currentUser?.actingUser : currentUser?.user;

    const tel = identity?.tel || notifyInfo?.notificationPhoneNumber;
    const isUseProfileTel =
        !notifyInfo?.tel ||
        (notifyInfo?.tel &&
            notifyInfo.notificationPhoneNumber &&
            notifyInfo?.tel === notifyInfo.notificationPhoneNumber);

    useEffect(() => {
        if (hasSentCheckEmail && countDown > 0) {
            SI.current = setInterval(() => {
                setCountDown(countDown - 1);
            }, 1000);
            setResendButtonDisabled(true);
        } else if (SI.current) {
            setResendButtonDisabled(false);
            clearInterval(SI.current);
        }

        return () => {
            if (SI.current) {
                clearInterval(SI.current);
            }
        };
    }, [countDown, hasSentCheckEmail]);

    useEffect(() => {
        refetchCurrentUser();
    }, []);

    const handleFormFinish = useCallback(() => {
        formInstance
            .validateFields()
            .then(async (values: IUpdatePasswordFormData) => {
                if (user && user.email && !showPwsError) {
                    try {
                        trackHeapEvent('WebApp - My Account - Click - Update Password Attempt', {
                            isKlarityAccount,
                        });
                        const res = await changePassword({
                            oldPassword: values.oldPassword,
                            newPassword: values.password,
                        });
                        if (res && res.error) {
                            trackHeapEvent('WebApp - My Account - View - Update Password Failed', {
                                isKlarityAccount,
                                error: res.error,
                            });
                            message.error(res.error);
                        } else {
                            trackHeapEvent('WebApp - My Account - View - Update Password Success', {
                                isKlarityAccount,
                            });
                            message.success('Password updated successfully');
                            setEdit(false);
                            setShowForgetTip(false);
                        }
                    } catch (e) {
                        trackHeapEvent('WebApp - My Account - View - Update Password Error', {
                            isKlarityAccount,
                            error: e instanceof Error ? e.message : 'Unknown error',
                        });
                        // eslint-disable-next-line no-console
                        console.error(e);
                    }
                }
            })
            .catch((e) => {
                // eslint-disable-next-line no-console
                console.error(e);
            });
    }, [formInstance, showPwsError, user, isKlarityAccount]);

    const sendForgetFun = useCallback(async () => {
        if (user && user.email && !resendButtonDisabled) {
            setLoading(true);
            trackHeapEvent('WebApp - My Account - Click - Send Forgot Password', {
                isKlarityAccount,
                isResend: hasSentCheckEmail,
            });
            const result = await sendForget({
                email: user.email,
            });
            if (!result.error) {
                trackHeapEvent('WebApp - My Account - View - Send Forgot Password Success', {
                    isKlarityAccount,
                    isResend: hasSentCheckEmail,
                });
                setShowForgetTip(true);
                setCountDown(timer.current);
            } else {
                trackHeapEvent('WebApp - My Account - View - Send Forgot Password Failed', {
                    isKlarityAccount,
                    isResend: hasSentCheckEmail,
                    error: result.error,
                });
                message.error(result.error);
            }
            setLoading(false);
        }
    }, [resendButtonDisabled, user, isKlarityAccount, hasSentCheckEmail]);

    const sendButtonRender = useMemo(() => {
        return hasSentCheckEmail ? (
            <Button disabled={resendButtonDisabled} className={s.resetBtn} onClick={sendForgetFun}>
                {resendButtonDisabled ? `Resend ${countDown}` : 'Resend'}
            </Button>
        ) : (
            <div
                className={s.forget}
                onClick={() => {
                    sendForgetFun();
                    setHasSentCheckEmail(true);
                }}
            >
                Forgot password?
            </div>
        );
    }, [countDown, hasSentCheckEmail, resendButtonDisabled, sendForgetFun]);

    useEffect(() => {
        telFormInstance.setFieldsValue({
            tel,
        });
    }, [tel, telFormInstance]);

    const handleSaveTel = async () => {
        telFormInstance
            .validateFields()
            .then(async (values: IUpdateTelFormData) => {
                const { tel: phoneNumber } = values;
                if (!isValidUSPhoneV2(phoneNumber)) {
                    trackHeapEvent('WebApp - My Account - View - Update Phone Invalid', {
                        isKlarityAccount,
                    });
                    message.error('Please enter a valid phone number.');
                    return;
                }
                try {
                    trackHeapEvent('WebApp - My Account - Click - Update Phone Attempt', {
                        isKlarityAccount,
                    });
                    const res = await updateUserPhone(phoneNumber);
                    if (res && res.error) {
                        trackHeapEvent('WebApp - My Account - View - Update Phone Failed', {
                            isKlarityAccount,
                            error: res.error,
                        });
                        message.error(res.error);
                    } else {
                        trackHeapEvent('WebApp - My Account - View - Update Phone Success', {
                            isKlarityAccount,
                        });
                        message.success('Phone number updated successfully');
                        refetch();
                        refetchCurrentUser();
                        setEditPhone(false);
                    }
                } catch (e) {
                    trackHeapEvent('WebApp - My Account - View - Update Phone Error', {
                        isKlarityAccount,
                        error: e instanceof Error ? e.message : 'Unknown error',
                    });
                    // eslint-disable-next-line no-console
                    console.error(e);
                }
            })
            .catch((e) => {
                // eslint-disable-next-line no-console
                console.error(e);
            });
    };

    if (editPhone && !isOpsPreview) {
        return (
            <Spin spinning={loading}>
                <div className={s.wrap}>
                    <div className={s.telLabel}>Phone numbers:</div>
                    <Form
                        form={telFormInstance}
                        name="form"
                        autoComplete="off"
                        layout="vertical"
                        onFinish={handleFormFinish}
                        scrollToFirstError
                    >
                        <Form.Item
                            label="This is your personal number"
                            name="tel"
                            style={{ width: '430px', marginBottom: '4px' }}
                            rules={[{ required: true, message: REQUIRED_FIELD }]}
                        >
                            <Input value={phoneValue} maxLength={18} onChange={(e) => setPhoneValue(e.target.value)} />
                        </Form.Item>
                    </Form>
                    <div className={s.acWrap}>
                        <Button style={{ marginRight: '16px' }} type="primary" onClick={handleSaveTel}>
                            Save
                        </Button>
                        <Button
                            onClick={() => {
                                trackHeapEvent('WebApp - My Account - Click - Cancel Phone Edit', {
                                    isKlarityAccount,
                                });
                                setEditPhone(false);
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </Spin>
        );
    }

    return (
        <Spin spinning={loading}>
            <div className={s.wrap}>
                <h3 className={s.title}>{isKlarityAccount ? 'Klarity Health' : 'Kiwi Health'}</h3>
                {edit && !isOpsPreview ? (
                    <Form
                        form={formInstance}
                        name="form"
                        autoComplete="off"
                        layout="vertical"
                        onFinish={handleFormFinish}
                        scrollToFirstError
                    >
                        <Form.Item
                            label="Old password"
                            name="oldPassword"
                            style={{ width: '430px', marginBottom: '4px' }}
                            rules={[
                                {
                                    required: true,
                                    message: 'old password is required',
                                },
                            ]}
                        >
                            <Input type="password" size="large" />
                        </Form.Item>
                        {showForgetTip && (
                            <div className={s.forgetTip}>
                                <div className={s.tipIcon} />
                                <div className={s.tipText}>
                                    An email with instructions to reset your password has been sent. If you don&apos;t
                                    receive the email, please click Resend.
                                </div>
                            </div>
                        )}
                        {sendButtonRender}
                        <Form.Item
                            label="New password"
                            name="password"
                            style={{ width: '430px' }}
                            rules={[
                                {
                                    required: true,
                                    message: 'new password is required',
                                },
                            ]}
                        >
                            <Input.Password
                                value={psw}
                                status={showPwsError ? 'error' : undefined}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const error: PasswordError[] = [];
                                    if (!hasLowerCase(val)) {
                                        error.push(PasswordError.LOWERCASE);
                                    }
                                    if (!hasNumber(val)) {
                                        error.push(PasswordError.NUMBER);
                                    }
                                    if (!hasSpecialCharacter(val)) {
                                        error.push(PasswordError.SPECIAL);
                                    }
                                    if (!hasUpperCase(val)) {
                                        error.push(PasswordError.UPPERCASE);
                                    }
                                    if (val.length < 8) {
                                        error.push(PasswordError.MINIMUM);
                                    }
                                    setPswError(error);
                                    setPsw(e.target.value);
                                }}
                                size="large"
                                style={{ marginBottom: '4px' }}
                            />
                        </Form.Item>
                        <div className={showPwsError ? `${s.pswError} ${s.pswErrorShow}` : s.pswError}>
                            <div>
                                <span
                                    className={
                                        pswError.includes(PasswordError.LOWERCASE) ? `${s.icon} ${s.iconError}` : s.icon
                                    }
                                />
                                One lowercase character
                            </div>
                            <div>
                                <span
                                    className={
                                        pswError.includes(PasswordError.SPECIAL) ? `${s.icon} ${s.iconError}` : s.icon
                                    }
                                />
                                One special character
                            </div>
                            <div>
                                <span
                                    className={
                                        pswError.includes(PasswordError.UPPERCASE) ? `${s.icon} ${s.iconError}` : s.icon
                                    }
                                />
                                One uppercase character
                            </div>
                            <div>
                                <span
                                    className={
                                        pswError.includes(PasswordError.MINIMUM) ? `${s.icon} ${s.iconError}` : s.icon
                                    }
                                />
                                8 characters minimum
                            </div>
                            <div>
                                <span
                                    className={
                                        pswError.includes(PasswordError.NUMBER) ? `${s.icon} ${s.iconError}` : s.icon
                                    }
                                />
                                One number
                            </div>
                        </div>
                        <Form.Item
                            label="Confirm password"
                            name="confirmPassword"
                            style={{ width: '430px' }}
                            rules={[
                                {
                                    validator: (rules, value) => {
                                        const newPwd = formInstance.getFieldValue('password');

                                        if (value !== newPwd) {
                                            return Promise.reject(
                                                new Error('The two passwords are entered differently'),
                                            );
                                        }

                                        return Promise.resolve(true);
                                    },
                                },
                            ]}
                        >
                            <Input type="password" size="large" />
                        </Form.Item>
                        <Form.Item>
                            <Button className={s.formBtn} type="primary" htmlType="submit">
                                Save
                            </Button>
                            <Button
                                className={s.formBtn}
                                onClick={() => {
                                    trackHeapEvent('WebApp - My Account - Click - Cancel Password Edit', {
                                        isKlarityAccount,
                                    });
                                    setEdit(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </Form.Item>
                    </Form>
                ) : (
                    <>
                        {isOpsPreview && (
                            // Wrapped in .item because the .tip rule is nested
                            // under .item in s.module.less — a bare .tip here
                            // would not match.
                            <div className={s.item}>
                                <div className={s.tip}>
                                    Read-only in preview mode. These credentials belong to the provider and can only be
                                    changed by them.
                                </div>
                            </div>
                        )}
                        <div className={s.item}>
                            <div className={s.Label}>Account email</div>
                            <div className={s.value}>{identity?.email || ''}</div>
                        </div>
                        <div className={s.item}>
                            <div className={s.Label}>Password</div>
                            <div className={s.value}>
                                **********
                                {!isOpsPreview && (
                                    <a
                                        className={s.action}
                                        onClick={() => {
                                            trackHeapEvent('WebApp - My Account - Click - Change Password', {
                                                isKlarityAccount,
                                            });
                                            setEdit(true);
                                        }}
                                    >
                                        Change
                                    </a>
                                )}
                            </div>
                        </div>
                        <div className={s.item}>
                            <div className={s.Label}>Phone number</div>
                            <div className={s.value}>
                                {tel}{' '}
                                {!isOpsPreview && (
                                    <a
                                        className={s.action}
                                        onClick={() => {
                                            trackHeapEvent('WebApp - My Account - Click - Change Phone', {
                                                isKlarityAccount,
                                            });
                                            setEditPhone(true);
                                        }}
                                    >
                                        Change
                                    </a>
                                )}
                            </div>
                            {isUseProfileTel && (
                                <div className={s.tip}>
                                    The number is now used for prospect notifications. You can configure the settings in
                                    the prospect section
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Hidden under OPS preview: switching mints a key bound to the
                    previewed provider, landing the operator in a plain provider
                    session on the sibling portal. Backend rejects it too — this
                    just keeps the dead button out of the UI. */}
                {isShowSwitchAccount && !isOpsPreview && <SwitchAccount isKlarityAccount={isKlarityAccount} />}
            </div>
        </Spin>
    );
};

export default MyAccount;
